import { Loader, Skeleton } from '@mantine/core';

export interface LoadingStateProps {
  /** What is loading, read to screen readers and shown under the spinner ("Loading the procedure"). */
  label?: string;
  /** cards = skeleton cards (lists, Home), rows = skeleton lines in one card, spinner = centred spinner. */
  variant?: 'cards' | 'rows' | 'spinner';
  /** How many skeleton cards or rows. */
  count?: number;
}

/** One skeleton card: a title line, two text lines and an action line. */
export function SkeletonCard({ lines = 2 }: { lines?: number }) {
  return (
    <div className="proc-card proc-skelcard" aria-hidden="true">
      <Skeleton height={18} width="55%" radius="sm" />
      {Array.from({ length: lines }, (_, i) => <Skeleton key={i} height={14} width={i % 2 ? '70%' : '90%'} radius="sm" />)}
      <Skeleton height={44} radius="md" mt={4} />
    </div>
  );
}

/** Skeleton lines inside one card (summary rows, settings). */
export function SkeletonRows({ count = 4 }: { count?: number }) {
  return (
    <div className="proc-card proc-skelcard" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '6px 0' }}>
          <Skeleton height={14} width="35%" radius="sm" />
          <Skeleton height={14} width="40%" radius="sm" />
        </div>
      ))}
    </div>
  );
}

/**
 * Loading placeholder with the right shape for the content that is coming, so the page doesn't
 * jump. Never show a status or hint until its data has arrived.
 */
export function LoadingState({ label = 'Loading', variant = 'cards', count = 2 }: LoadingStateProps) {
  return (
    <div className="proc-loading" role="status" aria-live="polite" aria-busy="true">
      {variant === 'spinner' ? (
        <div className="lbl"><Loader size="sm" aria-hidden="true" /> <span>{label}…</span></div>
      ) : (
        <>
          <span className="sr-only">{label}…</span>
          {variant === 'rows' ? <SkeletonRows count={count} /> : Array.from({ length: count }, (_, i) => <SkeletonCard key={i} />)}
        </>
      )}
    </div>
  );
}
